import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type StaleMark, covers, useStaleViewStore } from '@/hooks/websocket/useStaleViewStore';

// `JSON.stringify` escapes NUL inside strings, so it can never emit a bare one - which makes
// it a separator no serialized query key can contain. Built rather than written as a literal,
// so there is no invisible control character sitting in the source.
const SEPARATOR = String.fromCharCode(0);

/**
 * The marks the view on screen can actually act on.
 *
 * Marks are global - a broadcast about one asset marks `[assetData]` and `[trash]` together,
 * because both caches went out of date. The button is not global: it sits in the toolbar of
 * one view and offers to reload it. Standing in a folder, the bin's mark has nothing on
 * screen to reload, so it must not raise the button. (DM-1491)
 *
 * A query is "active" in react-query's sense - some mounted component is observing it - so
 * this tracks the view without the view having to declare itself.
 */
export const useActiveStaleMarks = (): Array<StaleMark> => {
  const queryClient = useQueryClient();
  const staleMarks = useStaleViewStore((state) => state.staleMarks);

  const subscribe = useCallback(
    (onStoreChange: () => void) => queryClient.getQueryCache().subscribe(onStoreChange),
    [queryClient],
  );

  // The snapshot carries the keys rather than merely signalling that they moved, so the memo
  // below has nothing to read behind React's back. A string, because the cache emits on every
  // fetch and every observer change while `useSyncExternalStore` compares snapshots by value -
  // so this re-renders only when the set of mounted queries actually changes.
  const getActiveKeys = useCallback(
    () =>
      queryClient
        .getQueryCache()
        .findAll({ type: 'active' })
        .map((query) => JSON.stringify(query.queryKey))
        .join(SEPARATOR),
    [queryClient],
  );

  const activeKeys = useSyncExternalStore(subscribe, getActiveKeys, getActiveKeys);

  return useMemo(() => {
    if (staleMarks.length === 0) {
      return staleMarks;
    }
    const active = activeKeys === '' ? [] : activeKeys.split(SEPARATOR).map((key) => JSON.parse(key) as QueryKey);
    return staleMarks.filter((mark) => active.some((activeKey) => covers(mark.queryKey, activeKey)));
  }, [staleMarks, activeKeys]);
};
